import { cloneDeep, get, set } from 'lodash';
import React, { useEffect, useState } from 'react';
import Login, { GetUserMe, Logout } from 'src/lib/api/AuthUser';
import { AUTH_TOKEN_DATA, BENUTZERDATEN_PORTAL_ROLLE, ERROR_MESSAGE, NOT_FOUND_ERROR_MESSAGE } from 'src/lib/constants/constant';
import { AuthenticatedDataType, AuthenticationContextType, AuthResponseType } from 'src/lib/types/types';
import { setAuthStore } from './AuthHelper';

/**
* Der `sessionStorage`-Schlüssel dient als einfacher Indikator dafür, dass sich der Benutzer zuvor in dieser Browsersitzung authentifiziert hat. Da das JWT in einem HttpOnly-Cookie gespeichert ist (auf das JavaScript keinen Zugriff hat), verwenden wir dieses Flag, um zu vermeiden, dass beim Laden der App ein vergeblicher Aufruf von `GetUserMe()` erfolgt, wenn sich der Benutzer noch nie angemeldet hat.
**/
const SESSION_FLAG_KEY = 'hasAuthSession';

// Create the Auth Context
const AuthenticationContext = React.createContext<AuthenticationContextType>({
	loading: false,
	isAuthenticated: false,
	authenticatedData: null,
	login: async () => ({ status: false }), // Default login implementation
	logout: () => {},
});

function AuthenticationProvider({ children }: { children?: React.ReactNode }) {
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
	const [authenticatedData, setAuthenticatedData] = useState<AuthenticatedDataType | null>(null);
	const [loading, setLoading] = useState<boolean>(true);

	async function login(identifier: string, password: string): Promise<AuthResponseType> {
		try {
			let res = await Login(identifier, password);
			if (res) {
				// Das JWT befindet sich nun im vom Server gesetzten „HttpOnly“-Cookie „accessToken“.
                // GetUserMe verwendet die Anmeldeinformationen: „include“, um das Cookie automatisch zu senden.
				let userDataRes = await GetUserMe();
				let tokenData: any = cloneDeep(res);
				set(tokenData, AUTH_TOKEN_DATA.personId, get(userDataRes, 'Person.id'));
				set(tokenData, AUTH_TOKEN_DATA.organisationId, get(userDataRes, 'Person.Organisation.id'));
				set(
					tokenData,
					AUTH_TOKEN_DATA.isHAUser,
					get(userDataRes, AUTH_TOKEN_DATA.PersonPortalBenutzerdatenPortalRolle, '') === BENUTZERDATEN_PORTAL_ROLLE.HA_USER ? true : false,
				);
				set(tokenData, AUTH_TOKEN_DATA.organisationName, get(userDataRes, 'Person.Organisation.Geschaeftsbezogene_Daten_Name_Der_Organisation'));
				sessionStorage.setItem(SESSION_FLAG_KEY, 'true');
				setAuthStore(tokenData); // Synchronisierung für Funktionen ohne Hook-Baumstruktur
				setAuthenticatedData(tokenData);
				setIsAuthenticated(true);
			}
			return {
				status: true,
			};
		} catch (err) {
			console.error('ERROR: Authentication user ', err);
			setAuthenticatedData(null);
			setIsAuthenticated(false);
			let errMsg: string | object;
			try {
				const message: string = get(err, 'message', '{}');
				errMsg = JSON.parse(message);
			} catch (err: any) {
				errMsg = err.message;
			}

			if (get(errMsg, 'error.message', '')) {
				return {
					status: false,
					errorMsg: get(ERROR_MESSAGE, get(errMsg, 'error.message', ''), NOT_FOUND_ERROR_MESSAGE),
				};
			} else {
				return {
					status: false,
					errorMsg: NOT_FOUND_ERROR_MESSAGE,
				};
			}
		} finally {
			setLoading(false);
		}
	}

	async function logout() {
		sessionStorage.removeItem(SESSION_FLAG_KEY);
		try {
			await Logout();
		} catch (err) {
			console.error('ERROR logging out: ', err);
		} finally {
			setAuthStore(null); // clear module-level
			setAuthenticatedData(null);
			setIsAuthenticated(false);
			setLoading(false);
		}
	}

	useEffect(() => {
		const fetchUserdata = async () => {
			if (!sessionStorage.getItem(SESSION_FLAG_KEY)) {
				setLoading(false);
				return;
			}

			try {
				let userDataRes = await GetUserMe();
				let tokenData: any = {};
				set(tokenData, AUTH_TOKEN_DATA.user, cloneDeep(userDataRes));
				set(tokenData, AUTH_TOKEN_DATA.personId, get(userDataRes, 'Person.id'));
				set(
					tokenData,
					AUTH_TOKEN_DATA.isHAUser,
					get(userDataRes, AUTH_TOKEN_DATA.PersonPortalBenutzerdatenPortalRolle, '') === BENUTZERDATEN_PORTAL_ROLLE.HA_USER ? true : false,
				);
				set(tokenData, AUTH_TOKEN_DATA.organisationId, get(userDataRes, 'Person.Organisation.id'));
				set(tokenData, AUTH_TOKEN_DATA.organisationName, get(userDataRes, 'Person.Organisation.Geschaeftsbezogene_Daten_Name_Der_Organisation'));
				setAuthStore(tokenData); // Synchronisierung für Funktionen ohne Hook-Baumstruktur
				setAuthenticatedData((prevValue: any) => {
					return { ...prevValue, ...tokenData };
				});
				setIsAuthenticated(true);
			} catch (err) {
				sessionStorage.removeItem(SESSION_FLAG_KEY);
				setAuthStore(null);
				setAuthenticatedData(null);
				setIsAuthenticated(false);
			} finally {
				setLoading(false);
			}
		};
		fetchUserdata();
	}, []);

	return <AuthenticationContext.Provider value={{ loading, isAuthenticated, authenticatedData, login, logout }}>{children}</AuthenticationContext.Provider>;
}

// Benutzerdefinierter Hook zur Verwendung des AuthContext
const useAuthentication = () => {
	const context = React.useContext(AuthenticationContext);
	if (context === undefined) {
		throw new Error('userAuth must be used within a AuthenticationProvider');
	}
	return context;
};

export default AuthenticationProvider;

export { AuthenticationContext, useAuthentication };
